import { useCallback, useMemo, CSSProperties } from 'react'
import { useDropzone } from 'react-dropzone'
import { arrayBufferToBase64 } from 'utils'
import RemoveButton from '../Buttons/RemoveButton'
import DragMessage from './DragMessage'

export type UploadType = {
  type: 'menu' | 'plumbing_plan' | 'delete' | 'delete_all'
  id: string
  uploadType?: 'menu' | 'plumbing_plan'
  name?: string
  size?: number
  mimeType?: string
  file?: string | ArrayBuffer
}

const baseStyle = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '20px',
  borderWidth: 2,
  borderRadius: 4,
  borderColor: '#8C8C8C',
  borderStyle: 'dashed',
  color: '#333333',
  outline: 'none',
  transition: 'border .24s ease-in-out'
}

const activeStyle = {
  borderColor: '#2196f3'
}

const acceptStyle = {
  borderColor: '#386323'
}

const rejectStyle = {
  borderColor: '#A20017'
}

interface DropZoneInterface {
  id: string
  type: 'menu' | 'plumbing_plan'
  maxFiles?: number
  maxSize?: number
  savedFiles?: Array<UploadType>
  handleError?: () => void
  handleUpload: (fileObj: UploadType) => void
  handleDelete: ({
    id,
    type
  }: {
    id: string
    type?: 'menu' | 'plumbing_plan'
  }) => void
  errorMessage?: string
}

function DropZone({
  id,
  type,
  maxFiles = 1,
  maxSize = 50000,
  savedFiles = [],
  handleError,
  handleUpload,
  handleDelete,
  errorMessage
}: DropZoneInterface) {
  const onDrop = useCallback(
    (acceptedFiles) => {
      acceptedFiles.slice(0, 5).forEach((file: any) => {
        const reader = new FileReader()
        reader.onabort = () => console.error('file reading was aborted')
        reader.onerror =
          handleError ?? (() => console.error('file upload error'))
        reader.onload = () => {
          // Do whatever you want with the file contents
          const arrayBuffer = reader.result
          const base64 = arrayBufferToBase64(arrayBuffer as ArrayBuffer)
          handleUpload({
            type,
            id: String(new Date().getTime()),
            name: file.name,
            size: file.size,
            mimeType: file.type,
            file: base64
          })
        }
        reader.readAsArrayBuffer(file)
      })
    },
    [handleError, handleUpload, type]
  )
  const {
    getRootProps,
    getInputProps,
    isDragActive,
    isDragAccept,
    isDragReject
  } = useDropzone({
    maxFiles,
    maxSize,
    accept: 'image/jpeg, image/png, application/pdf',
    onDrop
  })

  const style = useMemo(
    () => ({
      ...baseStyle,
      ...(isDragActive ? activeStyle : {}),
      ...(isDragAccept && (savedFiles.length ?? 0) < maxFiles
        ? acceptStyle
        : {}),
      ...(isDragReject ? rejectStyle : {})
    }),
    [isDragActive, isDragReject, isDragAccept]
  )

  return (
    <section>
      <div {...getRootProps({ style: style as CSSProperties })}>
        <input {...getInputProps()} id={id} />
        <DragMessage
          isDragReject={isDragReject}
          isDragAccept={isDragAccept}
          isDragLimit={isDragAccept && savedFiles.length >= maxFiles}
        />
      </div>
      <span
        className="w-100 wc-assist wc-assist__bottom"
        style={{ margin: 0, padding: 0, textAlign: 'end' }}
      >
        Maximum file size {maxSize / 1024 / 1024}MB. {maxFiles} files limit.
      </span>
      {errorMessage && (
        <span className="field-validation-error wc-assist wc-assist__bottom wc-assist--error">
          {errorMessage}
        </span>
      )}
      <div className="w-100">
        {savedFiles.length ? <h3 className="h6">Accepted files</h3> : <></>}
        {savedFiles &&
          savedFiles.map((file) => (
            <div
              className="wc-msg--grey w-100 flex flex-wrap justify-between items-center"
              key={file.id}
              style={{ padding: '10px 20px', borderRadius: 4, marginBottom: 5 }}
            >
              <span>
                {file.name} -{' '}
                {Math.round(((file?.size ?? 1) / 1024 / 1024) * 1000) / 1000}MB
              </span>
              <button
                className="wc-link flex justify-center items-center"
                style={{ lineHeight: 0 }}
                onClick={() => {
                  handleDelete({ id: file.id, type })
                }}
              >
                <span style={{ marginRight: 10 }}>
                  <RemoveButton
                    id={file.id}
                    type={type}
                    handleRemove={handleDelete}
                    isDisabled={false}
                  />
                </span>
                Delete
              </button>
            </div>
          ))}
      </div>
    </section>
  )
}

export default DropZone
